import { useEffect, useRef, useState } from 'react';

const MERCURYO_PRODUCTION_SDK_URL = 'https://widget.mercuryo.io/embed.2.1.js';
const MERCURYO_SANDBOX_SDK_URL = 'https://sandbox-widget.mrcr.io/embed.2.1.js';
const MERCURYO_SANDBOX_CHECKOUT_HOST = 'sandbox-exchange.mrcr.io';
const sdkLoadPromises = new Map();

const snakeToCamelCase = (value) => (
  value.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
);

export function buildMercuryoWidgetOptions(checkoutUrl) {
  const url = new URL(checkoutUrl);
  const options = {};

  url.searchParams.forEach((value, key) => {
    options[snakeToCamelCase(key)] = value;
  });

  return options;
}

export function getMercuryoSDKURL(checkoutUrl) {
  const { hostname } = new URL(checkoutUrl);
  return hostname.toLowerCase() === MERCURYO_SANDBOX_CHECKOUT_HOST
    ? MERCURYO_SANDBOX_SDK_URL
    : MERCURYO_PRODUCTION_SDK_URL;
}

function resolveMercuryoWidget() {
  const widget = window.mercuryoWidget;
  if (!widget || typeof widget.run !== 'function') {
    throw new Error('Mercuryo SDK loaded without an available widget');
  }
  return widget;
}

export function loadMercuryoWidget(checkoutUrl) {
  if (window.mercuryoWidget && typeof window.mercuryoWidget.run === 'function') {
    return Promise.resolve(window.mercuryoWidget);
  }

  const sdkUrl = getMercuryoSDKURL(checkoutUrl);
  const pendingLoad = sdkLoadPromises.get(sdkUrl);
  if (pendingLoad) return pendingLoad;

  const loadPromise = new Promise((resolve, reject) => {
    let script = Array.from(document.scripts).find((candidate) => candidate.src === sdkUrl);
    const handleLoad = () => {
      script.dataset.mercuryoSdkState = 'loaded';
      try {
        resolve(resolveMercuryoWidget());
      } catch (error) {
        reject(error);
      }
    };
    const handleError = () => {
      script.dataset.mercuryoSdkState = 'error';
      reject(new Error('Failed to load the Mercuryo checkout SDK'));
    };

    if (script?.dataset.mercuryoSdkState === 'loaded') {
      handleLoad();
      return;
    }
    if (script?.dataset.mercuryoSdkState === 'error') {
      handleError();
      return;
    }

    const shouldAppendScript = !script;
    if (shouldAppendScript) {
      script = document.createElement('script');
      script.src = sdkUrl;
      script.async = true;
      script.dataset.mercuryoSdk = 'true';
    }

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (shouldAppendScript) {
      document.head.appendChild(script);
    }
  });

  sdkLoadPromises.set(sdkUrl, loadPromise);
  const clearPendingLoad = () => {
    if (sdkLoadPromises.get(sdkUrl) === loadPromise) {
      sdkLoadPromises.delete(sdkUrl);
    }
  };
  loadPromise.then(clearPendingLoad, clearPendingLoad);
  return loadPromise;
}

const MercuryoWidget = ({ checkoutUrl }) => {
  const hostRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const host = hostRef.current;
    let cancelled = false;

    if (!host) return undefined;
    host.replaceChildren();
    setError('');

    let options;
    try {
      options = buildMercuryoWidgetOptions(checkoutUrl);
    } catch (parseError) {
      setError('The Mercuryo checkout URL is invalid.');
      return () => host.replaceChildren();
    }

    loadMercuryoWidget(checkoutUrl)
      .then((widget) => {
        if (cancelled) return;
        try {
          widget.run({ ...options, host });
        } catch (initializationError) {
          setError('Failed to initialize the Mercuryo checkout.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load the Mercuryo checkout.');
        }
      });

    return () => {
      cancelled = true;
      host.replaceChildren();
    };
  }, [checkoutUrl]);

  return (
    <div className="w-full">
      {error && (
        <div className="px-6 py-12 text-center" role="alert">
          <p className="text-sm text-red-700">{error}</p>
          <a
            className="mt-4 inline-flex text-sm font-medium text-primary-600 hover:text-primary-700"
            href={checkoutUrl}
            rel="noreferrer"
            target="_blank"
          >
            Open Mercuryo checkout
          </a>
        </div>
      )}
      <div
        className={error ? 'hidden' : 'w-full'}
        data-mercuryo-widget-host
        ref={hostRef}
        style={{ minHeight: '720px' }}
      />
    </div>
  );
};

export default MercuryoWidget;
